import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../styles/components/FormPage.css';
import '../../styles/components/RatingForm.css';
import '../../styles/components/VendorSuggestionForm.css';
import '../../styles/components/VendorFormPage.css';
import { Input as FormInput, Select as FormSelect, Textarea as FormTextarea } from '../FormField';
import Icon from '../Icon';
import RichTextEditor from '../ui/RichTextEditor';
import SidebarPhotoCard from '../ui/SidebarPhotoCard';
import TagMultiSelect from '../ui/TagMultiSelect';
import { PRICE_RANGE_OPTIONS } from '../../data/vendorData';
import type { ParsedVendor } from '../../data/vendorData';
import { saveRating } from '../../utils/ratingStorage';
import { useNotifications } from '../NotificationProvider';
import { loadVendorCategories, refreshVendorCategoriesFromApi } from '../../services/VendorCategoryService';
import RecordActivity from '../ui/RecordActivity';
import { useFormSaveEvent, useClearErrorsOnEdit } from './_shared';
import { uploadImage, deleteAttachment, getAttachments, resolveMediaUrl, type Attachment } from '../../services/uploadService';
import { updateVendorProfile, updateVendorRateCard } from '../../data/VendorLoader';
import { PHONE_PLACEHOLDER } from '../../constants/placeholders';

const MARKETPLACE_OPTIONS = [
  { value: '1688', label: '1688' },
  { value: 'aliexpress', label: 'AliExpress' },
  { value: 'alibaba', label: 'Alibaba' },
  { value: 'taobao', label: 'Taobao' },
  { value: 'other', label: 'Other' },
];

interface VendorFormPageProps {
  vendor?: ParsedVendor | null;
  onClose: () => void;
  onSubmit?: (data: { name: string; description: string; priceRange: string; marketplace: string; contact: { instagram: string; website: string; phone: string; email: string }; services: string[]; specializations: string[]; availability: string[]; rating: number }) => void | Promise<unknown>;
}

// Services/specializations/availability come back as either plain strings or
// {name} objects depending on the endpoint — flattened here so nothing
// object-shaped reaches a text node (React error #31).
const vsNames = (list: unknown[] | undefined): string[] =>
  (list ?? []).map((s: unknown) => typeof s === 'string' ? s : (s as any)?.name ?? '').filter(Boolean);

function vsStateFor(vendor: ParsedVendor | null | undefined) {
  return {
    name: vendor?.name ?? '',
    description: vendor?.description ?? '',
    priceRange: vendor?.priceRange ?? '',
    marketplace: vendor?.marketplace ?? '',
    contact: {
      instagram: vendor?.contact?.instagram ?? '',
      website: vendor?.contact?.website ?? '',
      phone: vendor?.contact?.phone ?? '',
      email: vendor?.contact?.email ?? '',
    },
    services: vsNames(vendor?.services),
    specializations: vsNames(vendor?.specializations),
    availability: vsNames(vendor?.availability),
    rating: vendor?.rating ?? 0,
  };
}

export const VendorFormPage: React.FC<VendorFormPageProps> = ({ vendor, onClose, onSubmit }) => {
  const { showError, showSuccess } = useNotifications();

  const [vsData, setVsData] = useState(() => vsStateFor(vendor));

  const [rfData, setRfData] = useState({ name: '', email: '', rating: 5, comments: '' });
  const [rfSubmitted, setRfSubmitted] = useState(false);
  const [vendorImages, setVendorImages] = useState<string[]>([]);
  const [imageUploading, setImageUploading] = useState(false);

  // ── Rate Card ──
  // A vendor's standing brochure/price list — lives here (not on a Quote)
  // because it's reference material that outlasts any single engagement.
  const [rateCardAtts, setRateCardAtts] = useState<Attachment[]>([]);
  const [rateCardUploading, setRateCardUploading] = useState(false);
  const rateCardInputRef = useRef<HTMLInputElement>(null);
  const [rateCardNotes, setRateCardNotes] = useState(vendor?.rateCardNotes ?? '');
  const [rateCardValidUntil, setRateCardValidUntil] = useState(vendor?.rateCardValidUntil ?? '');

  // ── Shortlist Assessment ──
  // sentiment/shortlistRank/shortlistTier/bestFit all exist on ParsedVendor
  // and are populated from the API (281 vendors carry a real sentiment, 272
  // a written note, 32 a shortlist rank) — but nothing in this form ever
  // rendered them, so a planner's own assessment was invisible and
  // unrecoverable except by reading the database directly.
  const [sentiment, setSentiment] = useState(vendor?.sentiment ?? 'undecided');
  const [sentimentNote, setSentimentNote] = useState(vendor?.sentimentNote ?? '');
  const [shortlistRank, setShortlistRank] = useState(vendor?.shortlistRank != null ? String(vendor.shortlistRank) : '');
  const [shortlistTier, setShortlistTier] = useState(vendor?.shortlistTier ?? '');
  const [bestFit, setBestFit] = useState(vendor?.bestFit ?? '');

  // The fields above are seeded once, at mount. /vendors/:id → /vendors/:other
  // is a client-side navigation that reuses this instance, so without this the
  // previous vendor's details stay in the form. Keyed on the vendor's id, not
  // the object: `vendors.find(...)` hands us a fresh reference on every
  // background refresh, and resetting on those would wipe in-progress edits.
  const loadedVendorIdRef = useRef(vendor?.id);
  useEffect(() => {
    if (loadedVendorIdRef.current === vendor?.id) return;
    loadedVendorIdRef.current = vendor?.id;
    setVsData(vsStateFor(vendor));
    setRateCardNotes(vendor?.rateCardNotes ?? '');
    setRateCardValidUntil(vendor?.rateCardValidUntil ?? '');
    setSentiment(vendor?.sentiment ?? 'undecided');
    setSentimentNote(vendor?.sentimentNote ?? '');
    setShortlistRank(vendor?.shortlistRank != null ? String(vendor.shortlistRank) : '');
    setShortlistTier(vendor?.shortlistTier ?? '');
    setBestFit(vendor?.bestFit ?? '');
    setRateCardAtts([]);
    setVendorImages([]);
  }, [vendor?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!vendor?.id) return;
    getAttachments('vendor', vendor.id)
      .then(atts => setRateCardAtts(atts.filter(a => a.is_rate_card)))
      .catch(() => {});
  }, [vendor?.id]);

  const handleRateCardFile = useCallback(async (file: File) => {
    if (!vendor?.id) return;
    setRateCardUploading(true);
    try {
      const att = await uploadImage(file, { entityType: 'vendor', entityId: vendor.id, label: file.name, isRateCard: true });
      setRateCardAtts(prev => [...prev, att]);
    } catch {
      showError('Rate card upload failed. Please try again.');
    }
    setRateCardUploading(false);
  }, [vendor?.id, showError]);

  const handleRemoveRateCardAtt = useCallback(async (id: string) => {
    try { await deleteAttachment(id); } catch { /* ignore */ }
    setRateCardAtts(prev => prev.filter(a => a.id !== id));
  }, []);



  useEffect(() => {
    if (vendor?.images) {
      setVendorImages(vendor.images.filter(Boolean));
    }
  }, [vendor?.id]);

  const [vendorCatsState, setVendorCatsState] = useState<string[]>([]);
  useEffect(() => {
    const cats = loadVendorCategories();
    setVendorCatsState(cats.map((c: any) => c.name ?? c));
    refreshVendorCategoriesFromApi().then(fresh => {
      if (fresh?.length) setVendorCatsState(fresh.map((c: any) => c.name ?? c));
    }).catch(() => {});
  }, []);

  const allVsOptions = vendorCatsState.length > 0 ? vendorCatsState : [
    'Photography', 'Videography', 'Catering', 'Florals', 'Music & DJ', 'Decor', 'Venue',
    'Bridal Hair & Makeup', 'Wedding Cake', 'Officiant', 'Lighting', 'Transportation',
    'Photo Booth', 'Event Planning', 'Stationery',
  ];

  const vsInputChange = (field: string, value: any) => {
    if (field.startsWith('contact.')) {
      const key = field.split('.')[1];
      setVsData(prev => ({ ...prev, contact: { ...prev.contact, [key]: value } }));
    } else {
      setVsData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleImageFile = useCallback(async (file: File) => {
    setImageUploading(true);
    try {
      const att = await uploadImage(file, { entityType: 'vendor', entityId: vendor?.id || 'new', label: vsData.name || 'Vendor image' });
      setVendorImages(imgs => [...imgs, resolveMediaUrl(att.url)]);
    } catch (err) {
      console.error('Image upload failed:', err);
    }
    setImageUploading(false);
  }, [vendor?.id, vsData.name]);

  const handleSave = useCallback(async () => {
    if (!vsData.name.trim()) { showError('Vendor name is required'); return; }
    // Everything below waits for the API and throws on failure, so the shared
    // hook can show a 422's field errors under their fields and keep the form
    // open (it used to close, or toast a fixed "rate card" message).
    // Rate card lives on its own columns and its own endpoint, so it needs an
    // explicit write here — the page's Save used to close the form and silently
    // drop these two fields, which is why they were parked in the sidebar
    // behind a separate button.
    if (vendor?.id) {
      await updateVendorProfile(vendor.id, {
        ...vsData,
        sentiment,
        sentimentNote: sentimentNote.trim() || null,
        shortlistRank: shortlistRank.trim() ? Number(shortlistRank) : null,
        shortlistTier: shortlistTier || null,
        bestFit: bestFit.trim() || null,
      });
      await updateVendorRateCard(vendor.id, {
        rateCardNotes: rateCardNotes.trim() || null,
        rateCardValidUntil: rateCardValidUntil || null,
      });
    }
    await onSubmit?.(vsData);
    onClose();
  }, [vsData, vendor?.id, rateCardNotes, rateCardValidUntil, sentiment, sentimentNote, shortlistRank, shortlistTier, bestFit, onSubmit, onClose, showError]);

  const handleRatingSave = useCallback(async () => {
    if (!vendor || !rfData.name.trim() || !rfData.email.trim()) return;
    try {
      saveRating({
        vendorId: vendor.id,
        vendorName: vendor.name,
        userName: rfData.name,
        userEmail: rfData.email,
        rating: rfData.rating,
        comments: rfData.comments,
        timestamp: new Date().toISOString(),
      });
      setRfSubmitted(true);
      setRfData({ name: '', email: '', rating: 5, comments: '' });
      setTimeout(() => setRfSubmitted(false), 2500);
      showSuccess('Rating submitted');
    } catch {
      showError('Error submitting rating. Please try again.');
    }
  }, [rfData, vendor, showError, showSuccess]);

  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the vendor. Please try again.' });
  useClearErrorsOnEdit(
    {
      name: vsData.name, price_range: vsData.priceRange, marketplace: vsData.marketplace, description: vsData.description,
      instagram: vsData.contact.instagram, website: vsData.contact.website, phone: vsData.contact.phone, email: vsData.contact.email,
      rate_card_notes: rateCardNotes, rate_card_valid_until: rateCardValidUntil,
      sentiment, sentiment_note: sentimentNote, shortlist_rank: shortlistRank, shortlist_tier: shortlistTier, best_fit: bestFit,
    },
    fieldErrors,
  );

  return (
    <div className="detail-layout">
      <div className="inline-form-page">
        <div className="form-row">
          <FormInput
            label="Vendor Name"
            value={vsData.name}
            onChange={e => vsInputChange('name', e.target.value)}
            placeholder="Enter vendor name"
            error={fieldErrors.errorFor('name')}
            required
          />
          <FormSelect
            label="Price Range"
            value={vsData.priceRange}
            onChange={e => vsInputChange('priceRange', e.target.value)}
            placeholder="Select tier"
            error={fieldErrors.errorFor('price_range')}
            options={[...PRICE_RANGE_OPTIONS]}
          />
          <FormSelect
            label="Marketplace"
            value={vsData.marketplace}
            onChange={e => vsInputChange('marketplace', e.target.value)}
            placeholder="Select marketplace"
            error={fieldErrors.errorFor('marketplace')}
            options={MARKETPLACE_OPTIONS}
          />
        </div>

        <RichTextEditor
          label="Description"
          value={vsData.description}
          onChange={v => vsInputChange('description', v)}
          placeholder="Describe the vendor and their expertise"
          error={fieldErrors.errorFor('description')}
          rows={3}
        />

        <hr className="poe-divider" />
        <h3 className="poe-sub-heading">Contact</h3>

        <div className="form-row">
          <FormInput label="Instagram" error={fieldErrors.errorFor('instagram')} value={vsData.contact.instagram} onChange={e => vsInputChange('contact.instagram', e.target.value)} placeholder="@username" />
          <FormInput label="Website" type="url" error={fieldErrors.errorFor('website')} value={vsData.contact.website} onChange={e => vsInputChange('contact.website', e.target.value)} placeholder="https://example.com" />
        </div>
        <div className="form-row">
          <FormInput label="Phone" type="tel" error={fieldErrors.errorFor('phone')} value={vsData.contact.phone} onChange={e => vsInputChange('contact.phone', e.target.value)} placeholder={PHONE_PLACEHOLDER} />
          <FormInput label="Email" type="email" error={fieldErrors.errorFor('email')} value={vsData.contact.email} onChange={e => vsInputChange('contact.email', e.target.value)} placeholder="vendor@example.com" />
        </div>

        {vendor && (
          <>
            <hr className="poe-divider" />
            <h3 className="poe-sub-heading">Rate Card</h3>
            <p className="form-hint">
              Their standing brochure/price list — not tied to any one project. New
              quotes for this vendor show it as reference.
            </p>

            <div className="form-row form-row--split">
              <FormInput
                label="Valid Until"
                type="date"
                value={rateCardValidUntil}
                onChange={e => setRateCardValidUntil(e.target.value)}
                error={fieldErrors.errorFor('rate_card_valid_until')}
              />
            </div>
            <FormTextarea
              label="Rate Card Notes"
              value={rateCardNotes}
              onChange={e => setRateCardNotes(e.target.value)}
              error={fieldErrors.errorFor('rate_card_notes')}
              placeholder="Type in pricing by hand if there's no file to attach…"
              rows={4}
            />

            {rateCardAtts.length > 0 && (
              <div className="vfp-items-list">
                {rateCardAtts.map(att => (
                  <div key={att.id} className="vfp-contract-uploaded">
                    <a href={resolveMediaUrl(att.url)} target="_blank" rel="noopener noreferrer" className="vfp-contract-link">
                      <Icon name="document" size={14} /> {att.label ?? att.original_name ?? 'Rate card file'} ↗
                    </a>
                    <button type="button" className="vfp-contract-remove" onClick={() => handleRemoveRateCardAtt(att.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            <div className="vfp-rate-card-actions">
              <button
                type="button"
                className="header-action-btn vfp-action-btn"
                onClick={e => { e.preventDefault(); rateCardInputRef.current?.click(); }}
              >
                {rateCardUploading ? 'Uploading…' : '+ Add Rate Card File'}
              </button>
            </div>
            <input
              ref={rateCardInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              className="u-hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) void handleRateCardFile(f); }}
            />

            <hr className="poe-divider" />
            <h3 className="poe-sub-heading">Shortlist Assessment</h3>
            <p className="form-hint">
              Your own read on this vendor — sentiment, rank against others in
              the same category, and why. Set for a ranked handful only; blank
              means no assessment yet.
            </p>
            <div className="form-row">
              <FormSelect
                label="Sentiment"
                value={sentiment}
                onChange={e => setSentiment(e.target.value as typeof sentiment)}
                error={fieldErrors.errorFor('sentiment')}
                options={[
                  { value: 'undecided', label: 'Undecided' },
                  { value: 'positive', label: 'Positive' },
                  { value: 'neutral', label: 'Neutral' },
                  { value: 'negative', label: 'Negative' },
                ]}
              />
              <FormSelect
                label="Shortlist Tier"
                value={shortlistTier}
                onChange={e => setShortlistTier(e.target.value)}
                error={fieldErrors.errorFor('shortlist_tier')}
                placeholder="Not ranked"
                options={[
                  { value: 'top', label: 'Top choice' },
                  { value: 'contender', label: 'Contender' },
                  { value: 'backup', label: 'Backup' },
                  { value: 'ruled-out', label: 'Ruled out' },
                ]}
              />
              <FormInput
                label="Shortlist Rank"
                type="number"
                min="1"
                value={shortlistRank}
                onChange={e => setShortlistRank(e.target.value)}
                error={fieldErrors.errorFor('shortlist_rank')}
                placeholder="e.g. 1"
              />
            </div>
            <div className="form-row">
              <FormInput
                label="Best Fit For"
                value={bestFit}
                onChange={e => setBestFit(e.target.value)}
                error={fieldErrors.errorFor('best_fit')}
                placeholder="e.g. Traditional wedding, budget-conscious"
              />
            </div>
            <FormTextarea
              label="Sentiment Notes"
              value={sentimentNote}
              onChange={e => setSentimentNote(e.target.value)}
              error={fieldErrors.errorFor('sentiment_note')}
              placeholder="Why this sentiment — what stood out, what didn't…"
              rows={3}
            />

            <hr className="poe-divider" />
            <h3 className="poe-sub-heading">Rate this Vendor</h3>
            {rfSubmitted ? (
              <div className="rating-success">
                <div className="success-icon">✓</div>
                <p>Rating submitted — thank you!</p>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <FormInput label="Your Name" value={rfData.name} onChange={e => setRfData(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Amaka Johnson" required />
                  <FormInput label="Email" type="email" value={rfData.email} onChange={e => setRfData(d => ({ ...d, email: e.target.value }))} placeholder="e.g. amaka@example.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Rating <span className="form-required">*</span></label>
                  <div className="rating-input">
                    {[1,2,3,4,5].map(star => (
                      <button key={star} type="button" className={`star-btn ${star <= rfData.rating ? 'active' : ''}`} onClick={() => setRfData(d => ({ ...d, rating: star }))}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      </button>
                    ))}
                  </div>
                </div>
                <FormTextarea label="Comments" value={rfData.comments} onChange={e => setRfData(d => ({ ...d, comments: e.target.value }))} placeholder="Share your experience…" rows={3} />
                <div className="vfp-action-row">
                  <button type="button" className="header-action-btn primary vfp-action-btn" onClick={handleRatingSave} disabled={!rfData.name.trim() || !rfData.email.trim()}>
                    Submit Rating
                  </button>
                </div>
              </>
            )}

            <RecordActivity entityType="vendor" entityId={vendor?.id} heading="Team Comments" fullWidth />
          </>
        )}
      </div>

      <aside className="detail-sidebar">
        <SidebarPhotoCard
          url={vendorImages.length > 0 ? resolveMediaUrl(vendorImages[0]) : undefined}
          alt="Vendor"
          uploading={imageUploading}
          onFileSelect={handleImageFile}
          onRemove={() => setVendorImages(imgs => imgs.slice(1))}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleImageFile(f); }}
        />

        <section className="side-section">
          <h4>Services</h4>
          <TagMultiSelect
            values={vsData.services}
            options={allVsOptions}
            onChange={services => setVsData(prev => ({ ...prev, services }))}
            placeholder="Search and select…"
            allowCreate={true}
          />
        </section>

        <section className="side-section">
          <h4>Specializations</h4>
          <TagMultiSelect
            values={vsData.specializations}
            options={allVsOptions}
            onChange={specializations => setVsData(prev => ({ ...prev, specializations }))}
            placeholder="Select specializations…"
            allowCreate={true}
          />
        </section>

        <section className="side-section">
          <h4>Availability</h4>
          <TagMultiSelect
            values={vsData.availability}
            options={['Weekdays', 'Weekends', 'Evenings', 'Holidays', 'Year-round', 'By appointment', 'Flexible']}
            onChange={availability => setVsData(prev => ({ ...prev, availability }))}
            placeholder="Select availability…"
            allowCreate={true}
          />
        </section>

        <section className="side-section">
          <h4>Rating</h4>
          <div className="vfp-star-row">
            {[1,2,3,4,5].map(star => (
              <button key={star} type="button" className={`star-btn ${star <= vsData.rating ? 'active' : ''}`} onClick={() => vsInputChange('rating', star)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              </button>
            ))}
          </div>
          <div className="stat-row vfp-rating-score-row">
            <span>Score</span><strong>{vsData.rating}/5</strong>
          </div>
        </section>
      </aside>
    </div>
  );
};

export { VendorFormPage as VendorSuggestionFormPage };
export { VendorFormPage as VendorProfileFormPage };
export { VendorFormPage as VendorRatingFormPage };
